import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { IconX, IconUpload } from '@tabler/icons-react';
import { SupportType } from '@/types/apps/ticket';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface CreateTicketModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  open,
  onClose,
  onSuccess,
  onError,
}) => {
  const [supportType, setSupportType] = useState<SupportType>(
    SupportType.GENERAL
  );
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  // eslint-disable-next-line no-undef
  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const [errors, setErrors] = useState<{
    supportType?: string;
    subject?: string;
    message?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  // eslint-disable-next-line no-undef
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!supportType) {
      newErrors.supportType = t('tickets.createModal.supportType.required');
    }

    if (!subject.trim()) {
      newErrors.subject = t('tickets.createModal.subject.required');
    }

    if (!message.trim()) {
      newErrors.message = t('tickets.createModal.message.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await apiService.createTicket({
        supportType,
        subject: subject.trim(),
        message: message.trim(),
        file: file || undefined,
      });

      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      const errorMessage = error.message || t('tickets.createModal.failedToCreate');
      setSubmitError(errorMessage);
      
      // Викликаємо onError callback для показу алерту в батьківському компоненті
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;

    setSupportType(SupportType.GENERAL);
    setSubject('');
    setMessage('');
    setFile(null);
    setErrors({});
    setSubmitError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={600}>
            {t('tickets.createModal.title')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <IconX size="20" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {submitError && (
            <Alert variant="filled" severity="error" onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <TextField
            select
            fullWidth
            required
            label={t('tickets.createModal.supportType.label')}
            value={supportType}
            onChange={e => {
              setSupportType(e.target.value as SupportType);
              setErrors({ ...errors, supportType: undefined });
            }}
            error={!!errors.supportType}
            helperText={errors.supportType}
            disabled={isSubmitting}
          >
            <MenuItem value={SupportType.GENERAL}>{t('tickets.createModal.supportType.general')}</MenuItem>
            <MenuItem value={SupportType.FILE}>{t('tickets.createModal.supportType.file')}</MenuItem>
            <MenuItem value={SupportType.EVC}>{t('tickets.createModal.supportType.evc')}</MenuItem>
          </TextField>

          <TextField
            fullWidth
            required
            label={t('tickets.createModal.subject.label')}
            value={subject}
            onChange={e => {
              setSubject(e.target.value);
              setErrors({ ...errors, subject: undefined });
            }}
            placeholder={t('tickets.createModal.subject.placeholder')}
            error={!!errors.subject}
            helperText={errors.subject}
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            required
            label={t('tickets.createModal.message.label')}
            value={message}
            onChange={e => {
              setMessage(e.target.value);
              setErrors({ ...errors, message: undefined });
            }}
            multiline
            rows={4}
            placeholder={t('tickets.createModal.message.placeholder')}
            error={!!errors.message}
            helperText={errors.message}
            disabled={isSubmitting}
          />

          <Box>
            <Typography variant="subtitle2" mb={1}>
              {t('tickets.createModal.uploadFile.title')}
            </Typography>
            <Box
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                backgroundColor: dragActive ? 'action.hover' : 'transparent',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                opacity: isSubmitting ? 0.5 : 1,
                '&:hover': {
                  borderColor: isSubmitting ? 'grey.300' : 'primary.main',
                  backgroundColor: isSubmitting
                    ? 'transparent'
                    : 'action.hover',
                },
              }}
              onClick={() =>
                !isSubmitting && document.getElementById('file-input')?.click()
              }
            >
              <input
                id="file-input"
                type="file"
                hidden
                onChange={handleFileChange}
                disabled={isSubmitting}
              />
              <IconUpload size="48" style={{ marginBottom: 8, opacity: 0.5 }} />
              {file ? (
                <Typography variant="body1" color="primary">
                  {file.name}
                </Typography>
              ) : (
                <>
                  <Typography variant="body1" mb={1}>
                    {t('tickets.createModal.uploadFile.dragAndDrop')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('tickets.createModal.uploadFile.maxSize')}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
          {t('tickets.createModal.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !subject.trim() || !message.trim()}
          startIcon={
            isSubmitting ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isSubmitting ? t('tickets.createModal.creating') : t('tickets.createModal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTicketModal;
